import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { ButtonDirective, TooltipDirective } from '@wheere-front/shared-ui';
import { TranslateFn } from './config-message';
import { ConfigReadiness } from './site-configuration.service';

type SegmentState = 'done' | 'partial' | 'missing';

interface Segment {
  key: 'hardware' | 'positions' | 'files' | 'size';
  state: SegmentState;
}

/**
 * Compact readiness bar of the site configuration tab (variant "D" picked by Eddy on
 * 2026-09-10): one line with the ready count and what is still missing, the actions on the
 * right, and a four-segment gauge underneath. Designed to stay in view while the gateway
 * panel scrolls.
 */
@Component({
  selector: 'lib-readiness-bar',
  standalone: true,
  imports: [ButtonDirective, TooltipDirective, TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      *transloco="let t"
      class="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
      <div class="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5">
        <!-- Status: ready count + what is missing -->
        <div class="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
          <span class="inline-flex items-center gap-2 text-sm font-bold text-gray-900 dark:text-slate-100">
            <span
              class="inline-block size-2.5 rounded-full"
              [class.bg-green-500]="readiness().canValidate"
              [class.bg-amber-400]="!readiness().canValidate"></span>
            {{ t('siteConfiguration.readiness.readyCount', { count: readyCount() }) }}
          </span>
          @if (readiness().canValidate) {
            <span class="text-sm text-gray-500 dark:text-slate-400">{{ t('siteConfiguration.readiness.allSet') }}</span>
          } @else {
            <span class="truncate text-sm text-gray-500 dark:text-slate-400">{{
              t('siteConfiguration.readiness.missing', { list: missingText(t) })
            }}</span>
          }
        </div>

        @if (dirty()) {
          <span class="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-400">
            <i class="pi pi-clock text-xs"></i>{{ t('siteConfiguration.readiness.unsaved') }}
          </span>
        }

        @if (canClear() || canValidate()) {
          <div class="hidden h-6 w-px bg-gray-200 sm:block dark:bg-slate-700"></div>
        }

        @if (canClear()) {
          <button
            type="button"
            class="inline-flex size-8 items-center justify-center rounded-md text-rose-600 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-30 dark:text-rose-400 dark:hover:bg-rose-500/10"
            [disabled]="loading()"
            (click)="clearSite.emit()"
            [libTooltip]="t('siteConfiguration.readiness.clearSite')"
            [attr.aria-label]="t('siteConfiguration.readiness.clearSite')"
            libTooltipPosition="bottom">
            <i class="pi pi-trash text-sm"></i>
          </button>
        }

        @if (canValidate()) {
          <button
            libBtn
            variant="aquamarine"
            size="sm"
            type="button"
            [disabled]="!readiness().canValidate || loading()"
            (click)="validate.emit($event)"
            [libTooltip]="
              !readiness().canValidate ? t('siteConfiguration.readiness.missing', { list: missingText(t) }) : ''
            "
            libTooltipPosition="bottom">
            @if (loading()) {
              <svg class="size-3.5 shrink-0 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            } @else {
              <svg class="size-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            }
            {{ t('siteConfiguration.readiness.validate') }}
          </button>
        }
      </div>

      <!-- Four-segment gauge: hardware · positions · files · size (hover a segment for detail). -->
      <div
        class="flex h-1.5 gap-0.5 bg-gray-100 dark:bg-slate-900/60"
        role="list"
        [attr.aria-label]="t('siteConfiguration.readiness.ariaLabel')">
        @for (s of segments(); track s.key) {
          <span
            role="listitem"
            class="flex-1"
            [class.bg-green-500]="s.state === 'done'"
            [class.bg-amber-400]="s.state === 'partial'"
            [class.bg-rose-400]="s.state === 'missing'"
            [libTooltip]="segmentLabel(s, t)"
            libTooltipPosition="bottom"></span>
        }
      </div>
    </div>
  `,
})
export class ReadinessBarComponent {
  readiness = input.required<ConfigReadiness>();
  loading = input<boolean>(false);
  dirty = input<boolean>(false);
  /** Bouton « Validate » affiché (droit de publier le layout). */
  canValidate = input<boolean>(true);
  /** Bouton « Clear site » affiché (droit `site:write`). */
  canClear = input<boolean>(true);

  validate = output<Event>();
  clearSite = output<void>();

  /** Gateways positioned / total and antennas positioned / total, parsed from the positions label
   *  (technical label built by the service, never displayed as such). */
  private readonly positionCounts = computed(() => {
    const m = this.readiness().positions.label.match(/\((\d+)\/(\d+) gw · (\d+)\/(\d+) ant\)/);
    if (!m) return null;
    return { gwOk: +m[1], gwTotal: +m[2], antOk: +m[3], antTotal: +m[4] };
  });

  hasPartial = computed(() => {
    const c = this.positionCounts();
    if (!c) return false;
    const ok = c.gwOk + c.antOk;
    return ok > 0 && ok < c.gwTotal + c.antTotal;
  });

  readonly readyCount = computed(() => {
    const r = this.readiness();
    return [r.hardware.done, r.positions.done, r.files.done, r.size.done].filter(Boolean).length;
  });

  readonly segments = computed<Segment[]>(() => {
    const r = this.readiness();
    return [
      { key: 'hardware', state: r.hardware.done ? 'done' : 'missing' },
      { key: 'positions', state: r.positions.done ? 'done' : this.hasPartial() ? 'partial' : 'missing' },
      { key: 'files', state: r.files.done ? 'done' : r.files.count > 0 ? 'partial' : 'missing' },
      { key: 'size', state: r.size.done ? 'done' : 'missing' },
    ];
  });

  /** Tooltip of one gauge segment. */
  protected segmentLabel(s: Segment, t: TranslateFn): string {
    const r = this.readiness();
    switch (s.key) {
      case 'hardware':
        return r.hardware.done
          ? t('siteConfiguration.readiness.segment.hardwareDone', { count: this.positionCounts()?.gwTotal ?? 0 })
          : t('siteConfiguration.readiness.segment.hardwareMissing');
      case 'positions':
        return r.positions.done
          ? t('siteConfiguration.readiness.segment.positionsDone')
          : t('siteConfiguration.readiness.segment.positionsMissing', { detail: this.positionsDetail(t) });
      case 'files':
        return r.files.done
          ? t('siteConfiguration.readiness.segment.filesDone')
          : t('siteConfiguration.readiness.segment.filesPartial', { count: r.files.count, total: r.files.total });
      case 'size':
        return r.size.done
          ? t('siteConfiguration.readiness.segment.sizeDone')
          : t('siteConfiguration.readiness.segment.sizeMissing');
    }
  }

  /** Plain-language list of what still blocks validation. */
  protected missingText(t: TranslateFn): string {
    const r = this.readiness();
    const m: string[] = [];
    if (!r.hardware.done) m.push(t('siteConfiguration.readiness.missingPart.hardware'));
    if (!r.positions.done) {
      m.push(t('siteConfiguration.readiness.missingPart.positions', { detail: this.positionsDetail(t) }));
    }
    if (!r.files.done) {
      m.push(t('siteConfiguration.readiness.missingPart.files', { count: r.files.count, total: r.files.total }));
    }
    if (!r.size.done) m.push(t('siteConfiguration.readiness.missingPart.size'));
    return m.join(' · ');
  }

  private positionsDetail(t: TranslateFn): string {
    const c = this.positionCounts();
    if (!c) return t('siteConfiguration.readiness.incomplete');
    const parts: string[] = [];
    if (c.gwOk < c.gwTotal) {
      parts.push(t('siteConfiguration.readiness.gatewaysPositioned', { ok: c.gwOk, total: c.gwTotal }));
    }
    if (c.antOk < c.antTotal) {
      parts.push(t('siteConfiguration.readiness.antennasPositioned', { ok: c.antOk, total: c.antTotal }));
    }
    return parts.length ? parts.join(', ') : t('siteConfiguration.readiness.incomplete');
  }
}
